import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Space, Table, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import ReminderService from '../../services/ReminderService';
import { hasRole, openNotification } from '../../utils';
import TrashIcon from '../../assets/icons/trash.svg';
import AccountCell from '../Reminders/AccountCell';
import { getCreatorAccountCellProps } from '../Reminders/accountCellHelper';

const { TextArea } = Input;

interface NoteReminderRecord {
  id?: number | string;
  content?: string;
  createdAt?: string;
  account?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    phone?: string;
    id?: number | string;
  };
  accountId?: number | string;
}

interface NoteRemindersProps {
  entityId?: number | string | null;
  entityName?: string;
  mode?: 'admin' | 'user';
  onChange?: () => void;
  active?: boolean;
  refreshKey?: number | string;
}

const NoteReminders: React.FC<NoteRemindersProps> = ({
  entityId,
  entityName = 'proposal',
  mode = 'admin',
  onChange,
  active = true,
  refreshKey = 0,
}) => {
  const [notes, setNotes] = useState<NoteReminderRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const normalizedEntityName = useMemo(
    () => (entityName ?? 'proposal').toString().toUpperCase(),
    [entityName]
  );
  const isAdmin = useMemo(() => hasRole('ADMIN'), []);

  const fetchNotes = async () => {
    if (!entityId) {
      setNotes([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const params = {
        entityNames: normalizedEntityName,
        entityId: String(entityId),
        page: page - 1,
        size: pageSize,
      };
      const serviceCall =
        mode === 'admin'
          ? ReminderService.getAdminNoteReminders
          : ReminderService.getMyNoteReminders;
      const response: any = await serviceCall(params);
      const payload = response?.data ?? response ?? {};
      const list: NoteReminderRecord[] = Array.isArray(payload?.content)
        ? payload.content
        : Array.isArray(payload)
          ? payload
          : [];
      setNotes(list);
      const nextTotal =
        typeof payload?.totalElements === 'number'
          ? payload.totalElements
          : typeof payload?.total === 'number'
            ? payload.total
            : list.length;
      setTotal(nextTotal);
    } catch (error) {
      console.error('Notlar yüklenemedi:', error);
      setNotes([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!active) return;
    fetchNotes();
  }, [active, entityId, normalizedEntityName, mode, page, pageSize, refreshKey]);

  useEffect(() => {
    setPage(1);
  }, [entityId, normalizedEntityName]);

  const handlePageChange = (nextPage: number, nextSize?: number) => {
    setPage(nextPage);
    if (nextSize && nextSize !== pageSize) {
      setPageSize(nextSize);
    }
  };

  const handleAddNote = async () => {
    if (!entityId) {
      openNotification('error', 'Hata', 'Önce bir proposal seçin.');
      return;
    }
    if (!noteContent.trim()) {
      openNotification('error', 'Hata', 'Not içeriği boş bırakılamaz.');
      return;
    }
    setSubmitting(true);
    try {
      const reminderDate = dayjs().format('YYYY-MM-DDTHH:mm:ss');
      await ReminderService.createForMe({
        entityName: normalizedEntityName,
        entityId: String(entityId),
        activityType: 'NOTE',
        channel: 'NOTE',
        content: noteContent.trim(),
        reminderDate,
      });
      openNotification('success', 'Not eklendi', 'Not kaydedildi.');
      setNoteContent('');
      setPage(1);
      fetchNotes();
      if (onChange) onChange();
    } catch (error) {
      console.error('Not kaydedilemedi:', error);
      openNotification('error', 'Hata', 'Not kaydedilemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId?: number | string) => {
    if (!noteId) return;
    try {
      await ReminderService.remove(noteId);
      openNotification('success', 'Başarılı', 'Not başarıyla silindi.');
      fetchNotes();
      if (onChange) onChange();
    } catch (error) {
      console.error('Not silinirken hata oluştu:', error);
      openNotification('error', 'Hata', 'Not silinirken bir hata oluştu.');
    }
  };

  const renderAccount = (record: NoteReminderRecord) => {
    return <AccountCell {...getCreatorAccountCellProps(record)} />;
  };

  const columns = [
    {
      title: 'Not',
      dataIndex: 'content',
      key: 'content',
      render: (value?: string) => value || '-',
    },
    {
      title: 'Kullanıcı',
      key: 'account',
      render: (_: any, record: NoteReminderRecord) => renderAccount(record),
    },
    {
      title: 'Tarih',
      dataIndex: 'reminderDate',
      key: 'reminderDate',
      render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
    },
    ...(mode === 'admin' && isAdmin
      ? [
          {
            title: 'İşlem',
            key: 'action',
            render: (_: any, record: NoteReminderRecord) => (
              <Popconfirm
                placement="left"
                title="Notu silmek istediğinize emin misiniz?"
                onConfirm={() => handleDeleteNote(record.id)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button
                  icon={<img src={TrashIcon} alt="trash" className="icon" />}
                  shape="circle"
                  className="icon-button ml-12px"
                />
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <TextArea
          value={noteContent}
          onChange={(event) => setNoteContent(event.target.value)}
          placeholder="Buraya notunuzu yazın..."
          rows={3}
          style={{ flex: 1 }}
        />
        <Button
          type="primary"
          onClick={handleAddNote}
          loading={submitting}
          disabled={!noteContent.trim()}
        >
          Not Ekle
        </Button>
      </div>
      <Table
        loading={loading}
        dataSource={notes}
        columns={columns as any}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: handlePageChange,
        }}
        rowKey={(record) => record.id ?? record.createdAt ?? Math.random().toString()}
        locale={{ emptyText: 'Henüz not yok.' }}
      />
    </Space>
  );
};

export default NoteReminders;
